import React, { useEffect, useMemo, useState } from 'react';
import Button from './Button';
import './PickSystemModal.css';

// CHOOSING THE SYSTEM AT EITHER END OF AN INTEGRATION.
//
// This was a panel beside the canvas, on the reasoning that the choice is made while looking at the
// diagram. With a real marketplace behind it that panel is a long unsearchable column that pushes the
// canvas off the screen, and the decision is short: find the system, pick it, carry on. A modal gives it
// room and a search box, and hands the canvas straight back.
//
// SOURCES ARE FILTERED. Only systems marked as an integration source on their marketplace record (V468)
// are offered on the left — the flag exists precisely so this list is a decision somebody made, rather
// than every system that happens to be active.
const PickSystemModal = ({ isOpen, role, systems, onPick, onClose }) => {
    const [search, setSearch] = useState('');

    useEffect(() => { if (isOpen) setSearch(''); }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    const eligible = useMemo(
        () => (systems || []).filter(sys => (role === 'SOURCE' ? sys.is_integration_source === 'Y' : true)),
        [systems, role]);

    const shown = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return eligible;
        return eligible.filter(sys => (sys.name || '').toLowerCase().includes(q));
    }, [eligible, search]);

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-md psm-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{role === 'SOURCE' ? 'Add a source' : 'Add a target'}</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                <div className="modal-body">
                    <p className="psm-hint">
                        {role === 'SOURCE'
                            ? 'The system that sends into this integration. Its proxy is drawn from its marketplace record.'
                            : 'The system that receives from the Universal API.'}
                    </p>

                    <input
                        className="psm-search"
                        placeholder="Search systems…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        autoFocus
                    />

                    {/* THREE DIFFERENT EMPTIES, THREE DIFFERENT SENTENCES. A palette that never loaded is not
                        a statement about any system's settings, and telling somebody to go and mark a record
                        that is already correct is worse than saying nothing. */}
                    {eligible.length === 0 ? (
                        <p className="psm-none">
                            {(systems || []).length === 0
                                ? 'The list of systems has not loaded. Save the integration and reopen it.'
                                : role === 'SOURCE'
                                    ? 'No system is marked as an integration source. Mark one on its marketplace '
                                      + 'record, ESB Integration tab — and remember to press Update.'
                                    : 'No active systems on the marketplace.'}
                        </p>
                    ) : shown.length === 0 ? (
                        <p className="psm-none">No system matches “{search.trim()}”.</p>
                    ) : (
                        <ul className="psm-list">
                            {shown.map(sys => (
                                <li key={sys.id}>
                                    <button type="button" className="psm-option" onClick={() => onPick(sys)}>
                                        <span className="psm-option-name">{sys.name}</span>
                                        {/* What comes with it, before the click rather than after: a system
                                            that needs a proxy arrives with one drawn in its path. */}
                                        {sys.uses_proxy === 'Y' && (
                                            <span className="psm-option-proxy">
                                                {sys.proxy_name ? `proxy: ${sys.proxy_name}` : 'proxy not named'}
                                            </span>
                                        )}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="modal-footer psm-footer">
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                </div>
            </div>
        </div>
    );
};

export default PickSystemModal;
